import { forwardRef } from "react";
import { Loader2, Plug, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { ChannelTypeBadge } from "./ChannelTypeBadge";
import type { ChannelType } from "@/modules/chatbot/types";

/**
 * ChannelConnectButton — kanal ulash CTA tugmasi.
 *
 * Token-aware (--channel-{type}-solid + --accent-gradient fallback).
 *
 * State'lar:
 *  - idle      → "Bog'lash"  (channel brand + Aurora gradient)
 *  - loading   → spinner
 *  - connected → "Ulangan"   (emerald)
 *  - error     → "Xato"      (rose)
 */

export type ChannelConnectState = "idle" | "loading" | "connected" | "error";

export interface ChannelConnectButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  channelType: ChannelType;
  /** @deprecated `state="loading"` ishlat */
  loading?: boolean;
  variant?: "primary" | "ghost";
  /** Override label */
  label?: string;
  /** Tugma holati */
  state?: ChannelConnectState;
  children?: React.ReactNode;
}

const STATE_LABEL: Record<ChannelConnectState, string> = {
  idle: "Bog'lash",
  loading: "Ulanmoqda...",
  connected: "Ulangan",
  error: "Xato — qayta urinish",
};

export const ChannelConnectButton = forwardRef<
  HTMLButtonElement,
  ChannelConnectButtonProps
>(
  (
    {
      channelType,
      loading,
      variant = "primary",
      label,
      state,
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const effectiveState: ChannelConnectState =
      state ?? (loading ? "loading" : "idle");
    const isLoading = effectiveState === "loading";
    const isConnected = effectiveState === "connected";
    const isError = effectiveState === "error";

    // Backround
    let background: string | undefined;
    if (variant === "primary") {
      if (isConnected) {
        background = "rgba(16, 185, 129, 0.18)";
      } else if (isError) {
        background = "rgba(244, 63, 94, 0.18)";
      } else {
        background = "var(--accent-gradient)";
      }
    }

    const colorCls = (() => {
      if (variant !== "primary") {
        return "border border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)]";
      }
      if (isConnected) {
        return "text-[var(--accent-emerald-fg)] border border-[var(--accent-emerald-border)] shadow-[var(--glow-success-sm)]";
      }
      if (isError) {
        return "text-[var(--accent-rose-fg)] border border-[var(--accent-rose-border)]";
      }
      return "text-white border border-transparent shadow-[var(--glow-sm)] hover:shadow-[var(--glow-md)]";
    })();

    const Icon = isConnected
      ? CheckCircle2
      : isError
        ? AlertCircle
        : isLoading
          ? Loader2
          : Plug;

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/60",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          colorCls,
          className,
        )}
        style={background ? { background } : undefined}
        {...props}
      >
        <Icon
          className={cn("h-4 w-4 shrink-0", isLoading && "animate-spin")}
          aria-hidden
        />
        <ChannelTypeBadge
          type={channelType}
          size="sm"
          showLabel
          className={
            variant === "primary" && !isConnected && !isError
              ? "!bg-white/15 !text-white !border-white/20"
              : undefined
          }
        />
        <span>{label ?? children ?? STATE_LABEL[effectiveState]}</span>
        {!isConnected && !isError && !isLoading && (
          <ArrowRight className="h-4 w-4 opacity-80" aria-hidden />
        )}
      </button>
    );
  },
);
ChannelConnectButton.displayName = "ChannelConnectButton";
